import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { setCurrentStudio, setToken } from '~entities/studios';
import { useStudioStore } from '~entities/studios/model/studio-store';
import { useTokenStore } from '~entities/studios/model/token-store';
import type { StudioDetails } from '~entities/studios/model/types';
import { useUserTokenStore } from '~entities/users/model/token-store';
import type { UserWithRole } from '~entities/users/model/types';
import {
  setCurrentUser,
  useUserStore,
} from '~entities/users/model/user-store';
import {
  CRM_BREAKOUT_PAYLOAD,
  CRM_BREAKOUT_REQUEST,
  type CrmBreakoutSessionPayload,
  decideCrmBreakoutWithoutOpener,
  safeCrmBreakoutReturnPath,
} from '~shared/utils/crm-embed-open-standalone';

const searchSchema = z.object({
  returnTo: z.string().optional(),
  locationId: z.string().optional(),
});

function isBreakoutPayload(
  data: unknown,
): data is CrmBreakoutSessionPayload {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const row = data as Record<string, unknown>;
  return (
    row.message === CRM_BREAKOUT_PAYLOAD && typeof row.token === 'string'
  );
}

function clearTopLevelStudioSession() {
  useTokenStore.setState({ token: null });
  useUserTokenStore.setState({ token: null });
  useStudioStore.setState({ currentStudio: null, currentStudioId: null });
  useUserStore.setState({ currentUser: null, currentUserId: null });
  localStorage.removeItem('token-storage');
  localStorage.removeItem('user-token-storage');
  localStorage.removeItem('studio-storage');
  localStorage.removeItem('user-storage');
}

/**
 * ENR-4101 / ENR-4102: top-level handoff page. Receives JWT + stores from the
 * CRM iframe opener (partitioned storage cannot be shared), then continues.
 *
 * Right-click "Open in new tab" has no opener — never keep a mismatched
 * top-level studio session (wrong currency + student 404s).
 */
function RouteComponent() {
  const { returnTo, locationId } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  const [loginHref, setLoginHref] = useState<string | null>(null);

  useEffect(() => {
    const origin = window.location.origin;
    const path = safeCrmBreakoutReturnPath(returnTo);
    const opener = window.opener as Window | null;

    if (!opener || opener === window) {
      const decision = decideCrmBreakoutWithoutOpener({
        returnTo,
        locationId,
        token: useTokenStore.getState().token,
        studioLocationId: useStudioStore.getState().currentStudio?.locationId,
      });

      if (decision.action === 'continue') {
        window.location.replace(decision.path);
        return;
      }

      if (decision.action === 'login') {
        clearTopLevelStudioSession();
        const params = new URLSearchParams({
          id: decision.locationId,
        });
        // Login only deep-links payment dashboard today; still pass redirect
        // for future support / standalone-dev.
        if (decision.redirect && decision.redirect !== '/') {
          params.set('redirect', decision.redirect);
        }
        window.location.replace(`/login?${params.toString()}`);
        return;
      }

      // Mismatched or missing session: clear wrong tenant so we never show
      // another studio's currency/records for this deep link.
      clearTopLevelStudioSession();
      if (decision.locationId) {
        const params = new URLSearchParams({ id: decision.locationId });
        if (path && path !== '/') {
          params.set('redirect', path);
        }
        setLoginHref(`/login?${params.toString()}`);
      }
      setError(
        'This browser tab does not have your CRM studio session. Go back to the CRM and use “Open in a new window” on the Studio banner, or sign in for this studio below.',
      );
      return;
    }

    let settled = false;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== opener) {
        return;
      }
      if (!isBreakoutPayload(event.data) || !event.data.token) {
        return;
      }
      settled = true;
      window.removeEventListener('message', onMessage);
      // Write into first-party (top-level) storage, then hard-navigate so the
      // rest of the app boots with a normal standalone session.
      setToken(event.data.token);
      if (event.data.user) {
        setCurrentUser(event.data.user as UserWithRole);
      }
      if (event.data.studio) {
        setCurrentStudio(event.data.studio as StudioDetails);
      }
      window.location.replace(path);
    };

    window.addEventListener('message', onMessage);
    opener.postMessage({ message: CRM_BREAKOUT_REQUEST }, origin);

    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        window.removeEventListener('message', onMessage);
        setError(
          'Timed out waiting for your CRM session. Close this tab and try Open in a new window again.',
        );
      }
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', onMessage);
    };
  }, [returnTo, locationId]);

  if (error) {
    return (
      <div className='flex h-screen items-center justify-center bg-gray-50 p-4'>
        <div className='max-w-md rounded-lg bg-white p-8 text-center shadow-sm'>
          <h2 className='mb-2 text-lg font-semibold text-gray-900'>
            Could not continue signed in
          </h2>
          <p className='text-sm text-gray-600'>{error}</p>
          {loginHref ? (
            <p className='mt-4'>
              <a
                href={loginHref}
                className='text-sm font-medium text-slate-900 underline'
              >
                Sign in for this studio
              </a>
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-screen items-center justify-center text-sm text-gray-600'>
      Opening Studio with your CRM session…
    </div>
  );
}

export const Route = createFileRoute('/crm-session-breakout')({
  validateSearch: searchSchema,
  component: RouteComponent,
});
